import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	Box,
	FormControlLabel,
	Radio,
	RadioGroup,
	Typography,
	Button,
	DialogActions,
} from '@mui/material';
import { CountrySelectMenu } from 'ui/CountrySelectMenu';
import countries from 'services/constants/countries-new.json';
import { HsCodeSelector } from 'components/Goods/GoodsEditDialog/HsCodeSelector';
import { IHsCodeItem } from 'redux/reducers/hscodes/types';
import { updateGoodsRequest } from 'redux/reducers/goods/reducer';
import { IUpdateGoodsPayload } from 'redux/reducers/goods/types';
import { RootState } from 'redux/store';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection } from 'services/constants/types';
import { EYesNo, PAGE_SIZE } from 'services/constants/env';
import { tableStructure } from '../tableStructure';

export const GoodsEditDialog: FC<{
	open: boolean;
	onClose: () => void;
	ids: number[];
}> = ({ open, onClose, ids }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();
	const { shopId } = useParams<{ shopId: string }>();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);

	const { loading, error } = useSelector((state: RootState) => state.goods);

	const [actDisclosedAgent, setActDisclosedAgent] = useState<EYesNo | null>(null);
	const [reducedVatTbe, setReducedVatTbe] = useState<EYesNo | null>(null);
	const [sellerRegistrationCountry, setSellerRegistrationCountry] = useState<string>('');
	const [originatingCountry, setOriginatingCountry] = useState<string>('');
	const [warehouseLocationCountry, setWarehouseLocationCountry] = useState<string>('');
	const [selectedHsCode, setSelectedHsCode] = useState<IHsCodeItem | null>(null);

	const eraseForm = () => {
		setSelectedHsCode(null);
		setActDisclosedAgent(null);
		setReducedVatTbe(null);
		setSellerRegistrationCountry('');
		setOriginatingCountry('');
		setWarehouseLocationCountry('');
	};

	const handleSave = () => {
		if (!shopId) return;
		const payload: IUpdateGoodsPayload = {
			body: {
				ids,
				hsCode: selectedHsCode?.code || '',
				sellerRegistrationCountry,
				originatingCountry,
				warehouseLocationCountry,
				isActDisclosedAgent: actDisclosedAgent ?? null,
				isReducedVatTbe: reducedVatTbe ?? null,
			},
			requestGoods: {
				shopId: +shopId,
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				sortingField:
					navigateParams.sortIndex !== null
						? (tableStructure[+navigateParams.sortIndex]?.selector ?? 'createdAt')
						: 'createdAt',
				sortingDirection: navigateParams?.sortDirection
					? navigateParams.sortDirection === ESortDirection.DESC
						? ESortDirection.DESC
						: ESortDirection.ASC
					: ESortDirection.DESC,
				name: String(navigateParams?.search ?? ''),
				sku: (navigateParams?.sku as string) ?? null,
				hsCode: (navigateParams?.hsCode as string) ?? null,
				isActDisclosedAgent: String(navigateParams?.isActDisclosedAgent ?? 'all'),
				isReducedVatTbe: String(navigateParams?.isReducedVatTbe ?? 'all'),
				sellerRegistrationCountry: (navigateParams?.sellerRegistrationCountry as string) ?? null,
				goodType: (navigateParams?.goodType as string) ?? null,
				originatingCountry: (navigateParams?.originatingCountry as string) ?? null,
				warehouseLocationCountry: (navigateParams?.warehouseLocationCountry as string) ?? null,
			},
		};

		dispatch(updateGoodsRequest(payload));
		eraseForm();
		onClose();
	};

	const handleCancel = () => {
		eraseForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleCancel} fullWidth maxWidth="md">
			<DialogTitle sx={{ fontWeight: 'bold', color: 'primary.main' }}>Edit Goods</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
					{error && (
						<Typography color="error" sx={{ mb: 2 }}>
							{error}
						</Typography>
					)}
					<HsCodeSelector value={selectedHsCode} onChange={setSelectedHsCode} />
					<CountrySelectMenu
						label="Seller Registration Country"
						onChange={setSellerRegistrationCountry}
						countryList={countries}
						countryCode={sellerRegistrationCountry}
					/>
					<CountrySelectMenu
						label="Originating Country"
						onChange={setOriginatingCountry}
						countryList={countries}
						countryCode={originatingCountry}
					/>
					<CountrySelectMenu
						label="Warehouse Location Country"
						onChange={setWarehouseLocationCountry}
						countryList={countries}
						countryCode={warehouseLocationCountry}
					/>
					<RadioGroup
						value={actDisclosedAgent === null ? '' : actDisclosedAgent.toString()}
						onChange={(e) => setActDisclosedAgent(e.target.value as EYesNo)}
						sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}
					>
						<Typography>Act as disclosed agent:</Typography>
						<FormControlLabel value={EYesNo.YES} control={<Radio />} label="Yes" />
						<FormControlLabel value={EYesNo.NO} control={<Radio />} label="No" />
					</RadioGroup>
					<RadioGroup
						value={reducedVatTbe === null ? '' : reducedVatTbe.toString()}
						onChange={(e) => setReducedVatTbe(e.target.value as EYesNo)}
						sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}
					>
						<Typography>Reduced VAT for TBE:</Typography>
						<FormControlLabel value={EYesNo.YES} control={<Radio />} label="Yes" />
						<FormControlLabel value={EYesNo.NO} control={<Radio />} label="No" />
					</RadioGroup>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button size="small" variant="outlined" color="primary" onClick={handleCancel}>
					Cancel
				</Button>
				<Button
					size="small"
					variant="contained"
					color="primary"
					onClick={handleSave}
					disabled={loading}
				>
					{loading ? 'Saving...' : 'Save'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
